import type { ExposureCategory } from '../../types/server/generics/exposure'
import { Box, Button, Input, SimpleGrid, Text } from '@chakra-ui/react'
import { LockIcon, UnLockIcon } from '../../assets/LockIcon'
import { CirclePlusIcon } from '../../assets/CirclePlusIcon'
import { TrashIcon } from '../../assets/TrashIcon'
import { useTranslation } from 'react-i18next'
import CompanyService from '../../services/company-service'
import Swal from 'sweetalert2'

import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type Props = {
	categories: Partial<ExposureCategory>[]
	setCategories: (categories: Partial<ExposureCategory>[]) => void
	isLite: boolean
}

const DraggableRisk = ({
	category,
	index,
	isLite,
	categories,
	setCategories,
}: {
	category: Partial<ExposureCategory>
	index: number
	isLite: boolean
	categories: Partial<ExposureCategory>[]
	setCategories: (c: Partial<ExposureCategory>[]) => void
}) => {
	const { t } = useTranslation()

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: category.id!, // id is set when items are created
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.7 : 1,
		cursor: isDragging ? 'grabbing' : 'grab',
	}

	return (
		<Box
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			pos='relative'
			className='p-2 bg-white rounded-lg shadow-sm hover:shadow-md duration-200'
		>
			<Text
				mb='5px'
				fontSize='14px'
				color='#aaa'
			>
				{t('modifyCompany.riskName')}
			</Text>

			<Box
				bg='#fff'
				border='1.2521px solid #E2E2E2'
				rounded='10px'
				display='flex'
				alignItems='center'
			>
				<Input
					border='none'
					value={category?.name}
					height='54px'
					isRequired
					onChange={(e) => {
						const updated = [...categories]
						updated[index] = { ...updated[index], name: e.target.value }
						setCategories(updated)
					}}
					maxLength={128}
				/>

				{isLite && (
					<Button
						bg='transparent'
						color={category.isLockedInLite ? '#990000' : '#3FB2D2'}
						onClick={() => {
							const updated = [...categories]
							updated[index] = {
								...updated[index],
								isLockedInLite: !category.isLockedInLite,
							}
							setCategories(updated)
						}}
					>
						{category.isLockedInLite ? <LockIcon /> : <UnLockIcon />}
					</Button>
				)}

				<Button
					bg='transparent'
					color='#3FB2D2'
					onClick={async () => {
						const result = await Swal.fire({
							title: `${t('modifyCompany.deleteRisk')} ${category.name}?`,
							text: t('modifyCompany.deleteWarning'),
							input: 'password',
							inputAttributes: {
								autocapitalize: 'off',
								placeholder: t('modifyCompany.enterPassword'),
							},
							showCancelButton: true,
							confirmButtonColor: '#3085d6',
							cancelButtonColor: '#d33',
							cancelButtonText: t('modifyCompany.cancel'),
							confirmButtonText: t('modifyCompany.delete'),
						})

						if (result.isConfirmed) {
							try {
								await CompanyService.checkAdminCode(result.value)
								setCategories(categories.filter((c) => c.id !== category.id))
							} catch {
								Swal.fire('', t('modifyCompany.invalidPassword'), 'error')
							}
						}
					}}
				>
					<TrashIcon />
				</Button>
			</Box>
		</Box>
	)
}

const Risks = ({ categories, setCategories, isLite }: Props) => {
	const { t } = useTranslation()
	const isPc = typeof window !== 'undefined' && window.innerWidth > 768

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const oldIndex = categories.findIndex((c) => c.id === active.id)
		const newIndex = categories.findIndex((c) => c.id === over.id)

		const reordered = arrayMove(categories, oldIndex, newIndex).map((c, idx) => ({
			...c,
			order: idx, // keep order in sync if you persist it later
		}))
		setCategories(reordered)
	}

	return (
		<DndContext
			collisionDetection={closestCenter}
			sensors={sensors}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={categories.map((c) => c.id!)}
				strategy={rectSortingStrategy}
			>
				<SimpleGrid
					columns={isPc ? 3 : 1}
					spacing={2}
				>
					{categories.map((category, i) => (
						<DraggableRisk
							key={category.id}
							category={category}
							index={i}
							isLite={isLite}
							categories={categories}
							setCategories={setCategories}
						/>
					))}

					{/* “Add Risk” tile – static (not sortable) */}
					<Box
						border='1.2521px dashed #3FB2D2'
						rounded='10px'
						justifyContent='center'
						alignItems='center'
						display='flex'
						gap='16px'
						mt='20px'
						p='10px'
						color='#3FB2D2'
						cursor='pointer'
						onClick={() =>
							setCategories([
								...categories,
								{
									id: (Math.random() + 1).toString(36).substring(2),
									name: `${t('modifyCompany.newRiskName')} ${categories.length + 1}`,
									isLockedInLite: false,
								},
							])
						}
					>
						<Text
							fontWeight='700'
							fontSize='18px'
						>
							{t('modifyCompany.addRisk')}
						</Text>
						<CirclePlusIcon />
					</Box>
				</SimpleGrid>
			</SortableContext>
		</DndContext>
	)
}

export default Risks
